import React, { useState, useRef } from 'react';
import { UploadCloud, X, CheckCircle2, AlertCircle } from 'lucide-react';
import type { WorkspaceFile } from '@sahaj/types';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useChat } from '../../context/ChatContext';
import { api } from '../../lib/api';

interface FileUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (file: WorkspaceFile) => void;
}

export const FileUploadModal: React.FC<FileUploadModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const { currentWorkspace } = useWorkspace();
  const { channels, directConversations } = useChat();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [targetType, setTargetType] = useState<'workspace' | 'channel' | 'dm'>('workspace');
  const [selectedChannelId, setSelectedChannelId] = useState<string>('');
  const [selectedConversationId, setSelectedConversationId] = useState<string>('');
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!isOpen || !currentWorkspace) return null;

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      validateAndSetFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      validateAndSetFile(e.target.files[0]);
    }
  };

  const validateAndSetFile = (file: File) => {
    setError(null);
    if (file.size > 50 * 1024 * 1024) {
      setError('File exceeds maximum size limit of 50 MB.');
      return;
    }
    setSelectedFile(file);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile || isUploading) return;

    try {
      setIsUploading(true);
      setError(null);

      const options: { channelId?: string; conversationId?: string } = {};
      if (targetType === 'channel' && selectedChannelId) {
        options.channelId = selectedChannelId;
      } else if (targetType === 'dm' && selectedConversationId) {
        options.conversationId = selectedConversationId;
      }

      const uploaded = await api.files.upload(currentWorkspace.id, selectedFile, options);
      onSuccess(uploaded);
      onClose();
    } catch (err: any) {
      console.error('File upload failed', err);
      setError(err?.response?.data?.error?.message || err?.response?.data?.message || err?.message || 'Failed to upload file');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-lg bg-card border border-border rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200 flex flex-col">
        {/* Header */}
        <div className="p-5 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2.5 font-bold text-base text-foreground">
            <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
              <UploadCloud className="h-4 w-4" />
            </div>
            <span>Upload File</span>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-muted text-muted-foreground transition"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Dropzone */}
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-2xl p-6 text-center cursor-pointer transition flex flex-col items-center justify-center ${
              isDragging
                ? 'border-primary bg-primary/10'
                : selectedFile
                ? 'border-emerald-500/50 bg-emerald-500/10'
                : 'border-border/80 bg-muted/20 hover:border-primary/50 hover:bg-primary/5'
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileChange}
              className="hidden"
            />

            {selectedFile ? (
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 rounded-xl bg-emerald-500/15 text-emerald-600 flex items-center justify-center mb-2 shadow-sm">
                  <CheckCircle2 className="h-6 w-6" />
                </div>
                <p className="text-sm font-semibold text-foreground max-w-xs truncate">{selectedFile.name}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{formatFileSize(selectedFile.size)}</p>
                <span className="text-[11px] text-primary font-medium mt-2 hover:underline">Click to replace file</span>
              </div>
            ) : (
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-2 shadow-sm">
                  <UploadCloud className="h-6 w-6" />
                </div>
                <p className="text-sm font-semibold text-foreground">
                  Click to select or drag and drop
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Supports documents, images, PDFs, code, audio, and video (up to 50 MB)
                </p>
              </div>
            )}
          </div>

          {error && (
            <div className="p-3 rounded-xl bg-destructive/10 border border-destructive/20 text-destructive text-xs flex items-center gap-2 font-medium">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Target destination */}
          <div>
            <label className="block text-xs font-semibold text-foreground mb-1.5">
              Share Destination
            </label>
            <div className="grid grid-cols-3 gap-2 mb-2">
              <button
                type="button"
                onClick={() => setTargetType('workspace')}
                className={`py-1.5 px-3 rounded-xl text-xs font-semibold border transition ${
                  targetType === 'workspace'
                    ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                    : 'bg-muted/70 text-muted-foreground border-border hover:bg-muted hover:text-foreground'
                }`}
              >
                Workspace
              </button>
              <button
                type="button"
                onClick={() => setTargetType('channel')}
                className={`py-1.5 px-3 rounded-xl text-xs font-semibold border transition ${
                  targetType === 'channel'
                    ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                    : 'bg-muted/70 text-muted-foreground border-border hover:bg-muted hover:text-foreground'
                }`}
              >
                Channel
              </button>
              <button
                type="button"
                onClick={() => setTargetType('dm')}
                className={`py-1.5 px-3 rounded-xl text-xs font-semibold border transition ${
                  targetType === 'dm'
                    ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                    : 'bg-muted/70 text-muted-foreground border-border hover:bg-muted hover:text-foreground'
                }`}
              >
                Direct Message
              </button>
            </div>

            {targetType === 'channel' && (
              <select
                value={selectedChannelId}
                onChange={(e) => setSelectedChannelId(e.target.value)}
                className="w-full h-10 px-3.5 bg-background border border-border rounded-xl text-xs font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition"
              >
                <option value="">Select a channel...</option>
                {channels.map((c) => (
                  <option key={c.id} value={c.id}>
                    #{c.name} {c.isPrivate ? '(Private)' : ''}
                  </option>
                ))}
              </select>
            )}

            {targetType === 'dm' && (
              <select
                value={selectedConversationId}
                onChange={(e) => setSelectedConversationId(e.target.value)}
                className="w-full h-10 px-3.5 bg-background border border-border rounded-xl text-xs font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition"
              >
                <option value="">Select a conversation...</option>
                {directConversations.map((d) => {
                  const label = d.title || d.members.map(m => m.displayName).join(', ');
                  return (
                    <option key={d.id} value={d.id}>
                      {label}
                    </option>
                  );
                })}
              </select>
            )}
          </div>

          {/* Footer Buttons */}
          <div className="pt-3 border-t border-border flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl text-xs font-medium text-muted-foreground hover:bg-muted transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!selectedFile || isUploading}
              className="px-5 py-2 rounded-xl bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition disabled:opacity-50 shadow-sm"
            >
              {isUploading ? 'Uploading...' : 'Upload File'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
